import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Link,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useApi } from "../../hooks/useApi";
import { formatUtcDateTime } from "../../utils/billingFormat";
import { ApiKey, ApiKeyCreateResult } from "../../types/apiKey";

const brandButtonSx = {
  backgroundColor: "#465fff",
  "&:hover": { backgroundColor: "#3641f5" },
} as const;

function CopyableField({ label, value, onCopy }: { label: string; value: string; onCopy: () => void }) {
  return (
    <TextField
      label={label}
      value={value}
      fullWidth
      size="small"
      slotProps={{
        input: {
          readOnly: true,
          sx: { fontFamily: "monospace", fontSize: "0.8125rem" },
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Copy">
                <IconButton edge="end" onClick={onCopy}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

interface ApiKeysManagerProps {
  apiBaseUrl: string;
}

export default function ApiKeysManager({ apiBaseUrl }: ApiKeysManagerProps) {
  const { get, post, del } = useApi();

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [createdKey, setCreatedKey] = useState<ApiKeyCreateResult | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>(
    { open: false, message: "", severity: "success" },
  );

  const fetchKeys = useCallback(async () => {
    setFetchError(null);
    setLoadingList(true);
    try {
      const response = await get<{ apiKeys: ApiKey[] }>(apiBaseUrl);
      setKeys(response.apiKeys);
    } catch (err: any) {
      setFetchError(err.message || "Failed to load API keys");
    } finally {
      setLoadingList(false);
    }
  }, [get, apiBaseUrl]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const notify = (message: string, severity: "success" | "error" = "success") =>
    setSnackbar({ open: true, message, severity });

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notify(`${label} copied to clipboard`);
    } catch {
      notify("Couldn't copy to clipboard", "error");
    }
  };

  const openCreate = () => {
    setName("");
    setCreateError(null);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setCreateError(null);
    try {
      const result = await post<ApiKeyCreateResult>(apiBaseUrl, { name: trimmed });
      setCreateOpen(false);
      setName("");
      setCreatedKey(result);
      fetchKeys();
    } catch (err: any) {
      setCreateError(err.message || "Failed to create API key");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget || revoking) return;
    setRevoking(true);
    setRevokeError(null);
    try {
      await del(`${apiBaseUrl}/${revokeTarget.client_id}`);
      setRevokeTarget(null);
      notify("API key revoked");
      fetchKeys();
    } catch (err: any) {
      setRevokeError(err.message || "Failed to revoke API key");
    } finally {
      setRevoking(false);
    }
  };

  const createButton = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={brandButtonSx}>
      Create API key
    </Button>
  );

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
        <Box sx={{ maxWidth: 620 }}>
          <Typography variant="body2" color="text.secondary">
            API keys let your applications authenticate with the Chargemont API. Each key's secret is shown only once,
            when you create it — store it somewhere safe.
          </Typography>
          <Link
            href="https://pubapi.chargemont.com/v1/docs"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.5, fontSize: "0.875rem", fontWeight: 500 }}
          >
            View API documentation
            <OpenInNewIcon sx={{ fontSize: 15 }} />
          </Link>
        </Box>
        {createButton}
      </Box>

      {fetchError && <Alert severity="error" sx={{ mb: 2 }}>{fetchError}</Alert>}

      {loadingList ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : keys.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 6, px: 3, textAlign: "center" }}>
          <VpnKeyOutlinedIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>No API keys yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a key to start integrating with the Chargemont API.
          </Typography>
          {createButton}
        </Paper>
      ) : (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Last used</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((key) => {
                  const revoked = key.status !== "active";
                  return (
                    <TableRow key={key.client_id} hover sx={revoked ? { opacity: 0.6 } : undefined}>
                      <TableCell>{key.name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography component="span" sx={{ fontFamily: "monospace", fontSize: "0.8125rem" }}>
                            {key.client_id}
                          </Typography>
                          <Tooltip title="Copy client ID">
                            <IconButton size="small" onClick={() => handleCopy(key.client_id, "Client ID")}>
                              <ContentCopyIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>{key.last_used_at ? formatUtcDateTime(key.last_used_at) : "Never"}</TableCell>
                      <TableCell>{formatUtcDateTime(key.created_at)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={revoked ? "Revoked" : "Active"}
                          color={revoked ? "default" : "success"}
                          variant={revoked ? "outlined" : "filled"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {!revoked && (
                          <Button size="small" color="error" onClick={() => { setRevokeError(null); setRevokeTarget(key); }} sx={{ textTransform: "none" }}>
                            Revoke
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={(_e, reason) => { if (reason !== "backdropClick") setCreateOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Create API key</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <TextField
            label="Name"
            placeholder="e.g. Production integration"
            helperText="A label to help you recognize this key later."
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
            fullWidth
            required
            autoFocus
            size="small"
            disabled={submitting}
            slotProps={{ htmlInput: { maxLength: 100 } }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={submitting || !name.trim()} sx={brandButtonSx}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* One-time secret reveal dialog */}
      <Dialog open={!!createdKey} maxWidth="sm" fullWidth>
        <DialogTitle>API key created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy your client secret now. For security, it won't be shown again — if you lose it, revoke this key and create a new one.
          </Alert>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <CopyableField
              label="Client ID"
              value={createdKey?.client_id ?? ""}
              onCopy={() => createdKey && handleCopy(createdKey.client_id, "Client ID")}
            />
            <CopyableField
              label="Client secret"
              value={createdKey?.client_secret ?? ""}
              onCopy={() => createdKey && handleCopy(createdKey.client_secret, "Client secret")}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreatedKey(null)} variant="contained" sx={brandButtonSx}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation dialog */}
      <Dialog open={!!revokeTarget} onClose={() => !revoking && setRevokeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Revoke API key</DialogTitle>
        <DialogContent>
          {revokeError && <Alert severity="error" sx={{ mb: 2 }}>{revokeError}</Alert>}
          <Typography>
            Revoke <strong>{revokeTarget?.name}</strong>? Applications using this key will immediately stop working.
            This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>Cancel</Button>
          <Button onClick={handleRevoke} variant="contained" color="error" disabled={revoking}>
            {revoking ? "Revoking…" : "Revoke"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
